import {
  addDays,
  addMinutes,
  addMonths,
  addWeeks,
  eachHourOfInterval,
  eachMinuteOfInterval,
  eachMonthOfInterval,
  eachWeekOfInterval,
  endOfMonth,
  endOfWeek,
  isBefore,
  isFriday,
  isSameDay,
  previousFriday,
  startOfDay,
} from 'date-fns'

import { Instrument } from '../../types/shared'
import { IntradayTimeframeInterval, TIMEFRAME } from '../constants/common'
import { Quote } from '../lib-export'
import { dateFromUnixTimestamp } from '../utils'

type HourRange = [number, number]
type DayHours = HourRange[] | null
type MarketHours = {
  [day: number]: DayHours
}

type WorkingMarketHours = {
  [timeframe in TIMEFRAME]: {
    [instrument in Instrument]: MarketHours
  }
}

const WORKING_HOURS_24_7: MarketHours = {
  0: [[0, 24]],
  1: [[0, 24]],
  2: [[0, 24]],
  3: [[0, 24]],
  4: [[0, 24]],
  5: [[0, 24]],
  6: [[0, 24]],
}

const DEFAULT_MARKET_HOURS = {
  [Instrument.Futures]: {
    0: [[18, 24]],
    1: [
      [0, 17],
      [18, 24],
    ],
    2: [
      [0, 17],
      [18, 24],
    ],
    3: [
      [0, 17],
      [18, 24],
    ],
    4: [
      [0, 17],
      [18, 24],
    ],
    5: [[0, 17]],
    6: null,
  } as MarketHours,
  [Instrument.Forex]: {
    0: [[17, 24]],
    1: [[0, 24]],
    2: [[0, 24]],
    3: [[0, 24]],
    4: [[0, 24]],
    5: [[0, 17]],
    6: null,
  } as MarketHours,
  // Crypto - 24/7
  [Instrument.Crypto]: {
    ...WORKING_HOURS_24_7,
  } as MarketHours,
}

const MARKET_HOURS = getMarketWorkingHours()

function getMarketWorkingHours() {
  const WORKING_MARKET_HOURS = {} as WorkingMarketHours

  for (const tf of Object.values(TIMEFRAME)) {
    WORKING_MARKET_HOURS[tf] = {} as WorkingMarketHours[keyof typeof TIMEFRAME]

    for (const inst of Object.keys(DEFAULT_MARKET_HOURS)) {
      const instrument = inst as keyof typeof DEFAULT_MARKET_HOURS
      if (tf === TIMEFRAME.h4) {
        const adjustedHours = {} as MarketHours
        for (const [day, hours] of Object.entries(DEFAULT_MARKET_HOURS[instrument])) {
          const dayNum = Number(day)
          if (hours === null) {
            adjustedHours[Number(dayNum)] = null
            continue
          }

          adjustedHours[Number(dayNum)] = hours.map(([start, end]) => {
            if (instrument === Instrument.Futures && dayNum === 0) {
              // 4hr futures data start on Sunday at 4pm
              return [16, end]
            }

            const adjustedStart = (start + 4) % 24
            return [adjustedStart, end]
          })
        }
        WORKING_MARKET_HOURS[tf][instrument] = adjustedHours
      } else {
        // For other timeframes, use original hours
        WORKING_MARKET_HOURS[tf][instrument] = DEFAULT_MARKET_HOURS[instrument]
      }
    }
  }

  return WORKING_MARKET_HOURS
}

export function isFuturesHoliday(date: Date): boolean {
  const year = date.getFullYear()

  // New Year's Day
  if (isObservedHoliday(date, year, 1, 1)) return true

  // Martin Luther King Jr. Day - 3rd Monday in January
  if (isNthWeekday(date, year, 1, 1, 3)) return true

  // Presidents' Day - 3rd Monday in February
  if (isNthWeekday(date, year, 2, 1, 3)) return true

  // Good Friday
  if (isGoodFriday(date)) return true

  // Memorial Day - Last Monday in May
  if (isLastWeekday(date, year, 5, 1)) return true

  // Juneteenth - June 19 (or observed)
  if (isObservedHoliday(date, year, 6, 19)) return true

  // Independence Day - July 4 (or observed)
  if (isObservedHoliday(date, year, 7, 4)) return true

  // Labor Day - 1st Monday in September
  if (isNthWeekday(date, year, 9, 1, 1)) return true

  // Thanksgiving Day - 4th Thursday in November
  if (isNthWeekday(date, year, 11, 4, 4)) return true

  // Christmas Day
  if (isObservedHoliday(date, year, 12, 25)) return true

  return false
}

export function isForexHoliday(date: Date): boolean {
  const year = date.getFullYear()

  // New Year's Day
  if (isObservedHoliday(date, year, 1, 1)) return true

  // Christmas Day
  if (isObservedHoliday(date, year, 12, 25)) return true

  return false
}

export function isCryptoTradingDay(): boolean {
  return true
}

export function isTradingDayByMarket(date: Date, timeframe: TIMEFRAME, marketType: Instrument): boolean {
  const dayOfWeek = date.getDay()
  const isIntraday = !!IntradayTimeframeInterval[timeframe as keyof typeof IntradayTimeframeInterval]

  switch (marketType) {
    case Instrument.Stock:
      if (dayOfWeek === 0 || dayOfWeek === 6) return false
      return !isStockHoliday(date)

    case Instrument.Futures:
    case Instrument.Group:
      if ((dayOfWeek === 0 && !isIntraday) || dayOfWeek === 6) return false
      return !isFuturesHoliday(date)

    case Instrument.Forex:
      if (dayOfWeek === 0 || dayOfWeek === 6) return false
      return !isForexHoliday(date)

    case Instrument.Crypto:
      return isCryptoTradingDay()

    default:
      return true
  }
}

export function isStockHoliday(date: Date): boolean {
  const year = date.getFullYear()

  // New Year's Day - January 1 (or observed)
  if (isObservedHoliday(date, year, 1, 1)) return true

  // Martin Luther King Jr. Day - 3rd Monday in January
  if (isNthWeekday(date, year, 1, 1, 3)) return true

  // Presidents' Day - 3rd Monday in February
  if (isNthWeekday(date, year, 2, 1, 3)) return true

  // Good Friday - Friday before Easter
  if (isGoodFriday(date)) return true

  // Memorial Day - Last Monday in May
  if (isLastWeekday(date, year, 5, 1)) return true

  // Juneteenth - June 19 (or observed)
  if (isObservedHoliday(date, year, 6, 19)) return true

  // Independence Day - July 4 (or observed)
  if (isObservedHoliday(date, year, 7, 4)) return true

  // Labor Day - 1st Monday in September
  if (isNthWeekday(date, year, 9, 1, 1)) return true

  // Thanksgiving Day - 4th Thursday in November
  if (isNthWeekday(date, year, 11, 4, 4)) return true

  // Christmas Day - December 25 (or observed)
  if (isObservedHoliday(date, year, 12, 25)) return true

  // The January 9 closure was a one-time event tied to President Carter's memorial service
  if (year === 2025 && isSameDay(date, new Date(2025, 0, 9))) return true

  return false
}

function isObservedHoliday(date: Date, year: number, month: number, day: number): boolean {
  const observedDate = getObservedHoliday(year, month, day)
  return isSameDay(date, observedDate)
}

function getObservedHoliday(year: number, month: number, day: number): Date {
  const holiday = new Date(year, month - 1, day)
  const dayOfWeek = holiday.getDay()

  if (dayOfWeek === 6) {
    // Saturday -> Friday
    return new Date(year, month - 1, day - 1)
  }
  if (dayOfWeek === 0) {
    // Sunday -> Monday
    return new Date(year, month - 1, day + 1)
  }

  return holiday
}

function isNthWeekday(date: Date, year: number, month: number, weekday: number, n: number): boolean {
  const nthDate = getNthWeekday(year, month, weekday, n)
  return nthDate ? isSameDay(date, nthDate) : false
}

function getNthWeekday(year: number, month: number, weekday: number, n: number): Date | null {
  const firstDay = new Date(year, month - 1, 1)
  const firstWeekday = firstDay.getDay()

  const daysToAdd = (weekday - firstWeekday + 7) % 7
  const targetDate = 1 + daysToAdd + (n - 1) * 7

  const result = new Date(year, month - 1, targetDate)

  return result.getMonth() === month - 1 ? result : null
}

function isLastWeekday(date: Date, year: number, month: number, weekday: number): boolean {
  const lastDate = getLastWeekday(year, month, weekday)
  return lastDate ? isSameDay(date, lastDate) : false
}

function getLastWeekday(year: number, month: number, weekday: number): Date | null {
  const lastDay = new Date(year, month, 0).getDate()

  for (let day = lastDay; day > lastDay - 7; day--) {
    const testDate = new Date(year, month - 1, day)
    if (testDate.getDay() === weekday) {
      return testDate
    }
  }

  return null
}

function isGoodFriday(date: Date): boolean {
  const year = date.getFullYear()
  const easter = calculateEaster(year)
  const goodFriday = new Date(easter)
  goodFriday.setDate(easter.getDate() - 2)

  return isSameDay(date, goodFriday)
}

function calculateEaster(year: number): Date {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const n = Math.floor((h + l - 7 * m + 114) / 31)
  const p = (h + l - 7 * m + 114) % 31

  return new Date(year, n - 1, p + 1)
}

export function getPreviousTradingDays(
  endDate: Date,
  count: number,
  timeframe: TIMEFRAME,
  instrument: Instrument
): Date[] {
  const tradingDays: Date[] = []
  const current = addDays(endDate, -1)
  current.setHours(0, 0, 0, 0)

  while (tradingDays.length < count) {
    if (isTradingDayByMarket(current, timeframe, instrument)) {
      tradingDays.unshift(new Date(current))
    }
    current.setDate(current.getDate() - 1)
  }

  return tradingDays
}

export function getNextTradingDays(
  startDate: Date,
  count: number,
  timeframe: TIMEFRAME,
  instrument: Instrument
): Date[] {
  const tradingDays: Date[] = []
  const current = addDays(startDate, 1)
  current.setHours(0, 0, 0, 0)

  while (tradingDays.length < count) {
    if (isTradingDayByMarket(current, timeframe, instrument)) {
      tradingDays.push(new Date(current))
    }
    current.setDate(current.getDate() + 1)
  }

  return tradingDays
}

function getEachDatetimeOfInterval({
  start,
  end,
  intervalInMinutes,
}: {
  start: Date
  end: Date
  intervalInMinutes: number
}) {
  const intervalProps = {
    start,
    end,
  }
  const isHrInterval = intervalInMinutes >= 60
  const eachDatetimeOfInterval = isHrInterval ? eachHourOfInterval(intervalProps) : eachMinuteOfInterval(intervalProps)

  const adjustedInterval = isHrInterval ? intervalInMinutes / 60 : intervalInMinutes
  return eachDatetimeOfInterval.filter((_, index) => index % adjustedInterval === 0)
}

export function getFutureTradingDates({ positionX, quote }: { positionX: number; quote: Quote }) {
  const interval = IntradayTimeframeInterval[quote.timeframe as keyof typeof IntradayTimeframeInterval]
  const quoteLength = quote.date.length
  const lastQuoteDate = dateFromUnixTimestamp(quote.date[quoteLength - 1])
  if (quote.timeframe === TIMEFRAME.m) {
    const numOfMonths = positionX + 1
    return [
      ...quote.date.map((d) => dateFromUnixTimestamp(d)),
      ...eachMonthOfInterval({
        start: addMonths(lastQuoteDate, 1),
        end: addMonths(lastQuoteDate, numOfMonths - quoteLength),
      }).map((date) => {
        let newDate = endOfMonth(date)
        newDate = isFriday(newDate) ? newDate : previousFriday(newDate)
        newDate.setHours(9, 0, 0, 0)
        return newDate
      }),
    ]
  }
  if (quote.timeframe === TIMEFRAME.w) {
    const numOfWeeks = positionX + 1
    return [
      ...quote.date.map((d) => dateFromUnixTimestamp(d)),
      ...eachWeekOfInterval(
        {
          start: addWeeks(lastQuoteDate, 1),
          end: addWeeks(lastQuoteDate, numOfWeeks - quoteLength),
        },
        {
          weekStartsOn: 1,
        }
      ).map((date) => {
        const newDate = previousFriday(endOfWeek(date, { weekStartsOn: 1 }))
        newDate.setHours(9, 0, 0, 0)
        return newDate
      }),
    ]
  }
  if (quote.timeframe === TIMEFRAME.d) {
    const newDates = getNextTradingDays(
      lastQuoteDate,
      Math.ceil(positionX - quoteLength + 1),
      quote.timeframe,
      quote.instrument
    )

    return [
      ...quote.date.map((d) => dateFromUnixTimestamp(d)),
      ...(quote.instrument === Instrument.Stock
        ? newDates.map((date) => {
            date.setHours(9, 0, 0, 0)
            return date
          })
        : newDates),
    ]
  }

  if (quote.instrument === Instrument.Stock) {
    const daysInQuote = quote.getDaysInQuote()

    const barsInDay = quote.getBarsInDay()
    const barsInChart = barsInDay * daysInQuote.length

    positionX += quote.barIndex[quote.barIndex.length - 1]
    const numOfNewBars = positionX - barsInChart + 1
    const newDaysInQuote = [
      ...daysInQuote,
      ...getNextTradingDays(
        daysInQuote[daysInQuote.length - 1],
        Math.ceil(numOfNewBars / barsInDay),
        quote.timeframe,
        quote.instrument
      ),
    ]
    return newDaysInQuote.flatMap((date) => {
      const dayStartDate = startOfDay(date)
      const startDate = addMinutes(dayStartDate, quote.marketStartMinutes)
      const endDate = addMinutes(dayStartDate, quote.marketStartMinutes + quote.drawMinutesPerDay - 1)

      const newDates = getEachDatetimeOfInterval({
        start: startDate,
        end: endDate,
        intervalInMinutes: interval,
      })

      if (quote.timeframe === TIMEFRAME.h || quote.timeframe === TIMEFRAME.h2 || quote.timeframe === TIMEFRAME.h4) {
        return newDates.map((date) => {
          date.setMinutes(date.getHours() === 9 ? 30 : 0, 0, 0)
          return date
        })
      }

      return newDates
    })
  }

  return [
    ...quote.date.map((d) => dateFromUnixTimestamp(d)),
    ...getNonStockIntradayFutureTradingDates({
      initialDate: addMinutes(lastQuoteDate, interval),
      instrument: quote.instrument,
      timeframe: quote.timeframe,
      interval,
      positionX,
    }),
  ]
}

function getNonStockIntradayFutureTradingDates({
  initialDate,
  timeframe,
  positionX,
  instrument,
  interval,
}: {
  instrument: Instrument
  timeframe: TIMEFRAME
  positionX: number
  interval: number
  initialDate: Date
}) {
  const cachedDates: Date[] = []
  const workingHours = MARKET_HOURS[timeframe][instrument] ?? WORKING_HOURS_24_7
  let remainingMinutes = Math.ceil((positionX + 1) * interval)
  let currentDate = new Date(initialDate)

  while (remainingMinutes > 0) {
    const dayOfWeek = currentDate.getDay()
    const dayConfig = workingHours[dayOfWeek as keyof typeof workingHours]

    if (!dayConfig || !isTradingDayByMarket(currentDate, timeframe, instrument)) {
      currentDate = startOfDay(addDays(currentDate, 1))
      continue
    }

    let currentMinutes = currentDate.getHours() * 60 + currentDate.getMinutes()
    let processedInThisDay = false

    for (const [startHour, endHour] of dayConfig) {
      const periodStart = startHour * 60
      const periodEnd = endHour * 60

      if (currentMinutes < periodStart) {
        currentDate.setHours(Math.floor(startHour), (startHour % 1) * 60, 0, 0)
      }

      currentMinutes = currentDate.getHours() * 60 + currentDate.getMinutes()

      if (currentMinutes >= periodEnd) {
        continue
      }

      const minutesLeftInPeriod = periodEnd - currentMinutes
      const minutesToAdd = Math.min(remainingMinutes, minutesLeftInPeriod)

      const initialCurrentDate = new Date(currentDate)
      currentDate.setMinutes(currentDate.getMinutes() + minutesToAdd)
      remainingMinutes -= minutesToAdd
      processedInThisDay = true

      cachedDates.push(
        ...getEachDatetimeOfInterval({
          start: initialCurrentDate,
          end: addMinutes(currentDate, -1),
          intervalInMinutes: interval,
        })
      )

      if (remainingMinutes === 0) {
        break
      }
    }

    if (!processedInThisDay) {
      currentDate = startOfDay(addDays(currentDate, 1))
    }
  }

  return cachedDates
}

export function getHistoryTradingDates({ positionX, quote }: { positionX: number; quote: Quote }) {
  const firstQuoteDate = dateFromUnixTimestamp(quote.date[0])
  if (quote.timeframe === TIMEFRAME.m) {
    const numOfMonths = positionX + 1
    const date = addMonths(firstQuoteDate, -numOfMonths)
    return eachMonthOfInterval({
      start: addMonths(dateFromUnixTimestamp(quote.date[0]), -1),
      end: date,
    }).map((d) => {
      let newDate = endOfMonth(d)
      newDate = isFriday(newDate) ? newDate : previousFriday(newDate)
      newDate.setHours(9, 0, 0, 0)
      return newDate
    })
  }
  if (quote.timeframe === TIMEFRAME.w) {
    const numOfWeeks = positionX + 1
    return eachWeekOfInterval(
      {
        start: addWeeks(firstQuoteDate, -1),
        end: addWeeks(firstQuoteDate, -numOfWeeks),
      },
      {
        weekStartsOn: 5,
      }
    ).map((date) => {
      const newDate = previousFriday(endOfWeek(date, { weekStartsOn: 1 }))
      newDate.setHours(9, 0, 0, 0)
      return newDate
    })
  }
  if (quote.timeframe === TIMEFRAME.d) {
    const numOfDays = positionX + 1
    const prevTradingDays = getPreviousTradingDays(
      firstQuoteDate,
      numOfDays,
      quote.timeframe,
      quote.instrument
    ).reverse()

    if (quote.instrument === Instrument.Stock) {
      return prevTradingDays.map((date) => {
        date.setHours(9, 0, 0, 0)
        return date
      })
    }
    return prevTradingDays
  }

  const interval = IntradayTimeframeInterval[quote.timeframe as keyof typeof IntradayTimeframeInterval]

  if (quote.instrument === Instrument.Stock) {
    const daysInQuote = quote.getDaysInQuote()

    const barsInDay = quote.getBarsInDay()

    const numOfNewBars = positionX + 1

    const prevTradingDays = getPreviousTradingDays(
      daysInQuote[0],
      Math.ceil(numOfNewBars / barsInDay),
      quote.timeframe,
      quote.instrument
    ).reverse()

    return prevTradingDays.flatMap((date) => {
      const dayStartDate = startOfDay(date)
      const startDate = addMinutes(dayStartDate, quote.marketStartMinutes)
      const endDate = addMinutes(dayStartDate, quote.marketStartMinutes + quote.drawMinutesPerDay - 1)

      const newDates = getEachDatetimeOfInterval({
        start: startDate,
        end: endDate,
        intervalInMinutes: interval,
      }).reverse()

      if (quote.timeframe === TIMEFRAME.h || quote.timeframe === TIMEFRAME.h2 || quote.timeframe === TIMEFRAME.h4) {
        return newDates.map((date) => {
          date.setMinutes(date.getHours() === 9 ? 30 : 0, 0, 0)
          return date
        })
      }

      return newDates
    })
  }

  const cachedDates: Date[] = []
  let remainingMinutes = Math.ceil((positionX + 1) * interval)
  let index = 0

  while (remainingMinutes > 0) {
    const futureTradingDays = getNonStockIntradayFutureTradingDates({
      initialDate: startOfDay(addDays(firstQuoteDate, -index)),
      instrument: quote.instrument,
      timeframe: quote.timeframe,
      interval,
      positionX: (24 * 60) / interval - 1,
    }).reverse()
    for (let i = 0; i < futureTradingDays.length; i++) {
      const date = futureTradingDays[i]
      if ((!cachedDates[0] || isBefore(date, cachedDates[0])) && isBefore(date, firstQuoteDate)) {
        cachedDates.unshift(date)
        remainingMinutes -= interval
      }
      if (remainingMinutes <= 0) {
        break
      }
    }
    index += 1
  }

  return cachedDates.reverse()
}
